import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, Server, Building2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { DocsSearchTrigger } from './DocsSearch';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileResourcesOpen, setIsMobileResourcesOpen] = useState(false);
  const [isMobileApiDocsOpen, setIsMobileApiDocsOpen] = useState(false);
  const [isMobileLoginOpen, setIsMobileLoginOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isMobileAboutOpen, setIsMobileAboutOpen] = useState(false);
  const dropdownTimeoutRef = useRef(null);
  const apiDropdownTimeoutRef = useRef(null);
  const loginDropdownTimeoutRef = useRef(null);
  const productsDropdownTimeoutRef = useRef(null);
  const aboutDropdownTimeoutRef = useRef(null);
  const [isDesktopResourcesOpen, setIsDesktopResourcesOpen] = useState(false);
  const [isDesktopApiDocsOpen, setIsDesktopApiDocsOpen] = useState(false);
  const [isDesktopLoginOpen, setIsDesktopLoginOpen] = useState(false);
  const [isDesktopProductsOpen, setIsDesktopProductsOpen] = useState(false);
  const [isDesktopAboutOpen, setIsDesktopAboutOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isDocsRoute =
    location.pathname.startsWith('/technical-documentation') ||
    location.pathname.startsWith('/payment-integration');

  // Every navigation from a top-nav dropdown carries this state, which lets
  // destination pages render their back button as "Back to Home" (→ '/')
  // instead of the default "Back" (→ -1). Other entry points (footer,
  // direct URL, deep links) won't carry this state, so their back-button
  // behaviour stays untouched.
  const TOP_NAV_STATE = { state: { from: 'top-nav' } };

  const productsItems = [
    { label: 'POS Card Machines',     action: () => navigate('/products/pos-card-machines', TOP_NAV_STATE) },
    { label: 'EPOS & EPOS Mini',      action: () => navigate('/products/epos-and-mini', TOP_NAV_STATE) },
    { label: 'SoftPOS & MPOS',        action: () => navigate('/products/softpos-and-mpos', TOP_NAV_STATE) },
    { label: 'eCommerce Solutions',   action: () => navigate('/products/ecommerce-solutions', TOP_NAV_STATE) },
    { label: 'Transaction Reporting', action: () => navigate('/products/transaction-reporting', TOP_NAV_STATE) },
  ];

  const resourcesItems = [
    { label: 'N86 Terminal User Guide', action: () => navigate('/n86-terminal-guide', TOP_NAV_STATE) },
    { label: 'N96 Terminal User Guide', action: () => navigate('/n96-terminal-guide', TOP_NAV_STATE) },
    { label: 'Business Forms & Docs', action: () => navigate('/business-forms', TOP_NAV_STATE) },
    { label: 'Accepting Payments FAQs', action: () => navigate('/accepting-payments-faqs', TOP_NAV_STATE) },
    { label: 'Help Centre', action: () => navigate('/help-centre', TOP_NAV_STATE) },
  ];

  const apiDocsItems = [
    { label: 'Payment Integration', action: () => navigate('/payment-integration', TOP_NAV_STATE) },
    { label: 'Technical Documentation', action: () => navigate('/technical-documentation', TOP_NAV_STATE) },
  ];

  const loginItems = [
    { label: 'ACP Login',          icon: Server,        action: () => navigate('/merchant-login/acp', TOP_NAV_STATE) },
    { label: 'ACQ Login',          icon: Building2,     action: () => navigate('/merchant-login/acq', TOP_NAV_STATE) },
  ];

  const aboutItems = [
    { label: 'Company',               action: () => navigate('/about/company', TOP_NAV_STATE) },
    { label: 'Partner With Us',       action: () => navigate('/about/partner-with-us', TOP_NAV_STATE) },
    { label: 'Social Responsibility', action: () => navigate('/about/social-responsibility', TOP_NAV_STATE) },
    { label: 'Ethical Business',      action: () => navigate('/about/ethical-business', TOP_NAV_STATE) },
    { label: 'Press & Media',         action: () => navigate('/about/press-and-media', TOP_NAV_STATE) },
  ];

  const handleAboutEnter = () => {
    if (aboutDropdownTimeoutRef.current) clearTimeout(aboutDropdownTimeoutRef.current);
    setIsDesktopAboutOpen(true);
    setIsDesktopResourcesOpen(false);
    setIsDesktopApiDocsOpen(false);
    setIsDesktopLoginOpen(false);
    setIsDesktopProductsOpen(false);
  };

  const handleAboutLeave = () => {
    aboutDropdownTimeoutRef.current = setTimeout(() => {
      setIsDesktopAboutOpen(false);
    }, 150);
  };

  const handleProductsEnter = () => {
    if (productsDropdownTimeoutRef.current) clearTimeout(productsDropdownTimeoutRef.current);
    setIsDesktopProductsOpen(true);
    setIsDesktopResourcesOpen(false);
    setIsDesktopApiDocsOpen(false);
    setIsDesktopLoginOpen(false);
  };

  const handleProductsLeave = () => {
    productsDropdownTimeoutRef.current = setTimeout(() => {
      setIsDesktopProductsOpen(false);
    }, 150);
  };

  const handleLoginEnter = () => {
    if (loginDropdownTimeoutRef.current) clearTimeout(loginDropdownTimeoutRef.current);
    setIsDesktopLoginOpen(true);
    setIsDesktopResourcesOpen(false);
    setIsDesktopApiDocsOpen(false);
    setIsDesktopProductsOpen(false);
  };

  const handleLoginLeave = () => {
    loginDropdownTimeoutRef.current = setTimeout(() => {
      setIsDesktopLoginOpen(false);
    }, 150);
  };

  const handleDropdownEnter = () => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setIsDesktopResourcesOpen(true);
    setIsDesktopApiDocsOpen(false);
    setIsDesktopProductsOpen(false);
  };

  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsDesktopResourcesOpen(false);
    }, 150);
  };

  const handleApiDropdownEnter = () => {
    if (apiDropdownTimeoutRef.current) clearTimeout(apiDropdownTimeoutRef.current);
    setIsDesktopApiDocsOpen(true);
    setIsDesktopResourcesOpen(false);
    setIsDesktopProductsOpen(false);
  };

  const handleApiDropdownLeave = () => {
    apiDropdownTimeoutRef.current = setTimeout(() => {
      setIsDesktopApiDocsOpen(false);
    }, 150);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const isAnyDesktopDropdownOpen =
    isDesktopResourcesOpen || isDesktopApiDocsOpen || isDesktopLoginOpen || isDesktopProductsOpen || isDesktopAboutOpen;

  return (
    <>
      {/* Hero dim scrim — only when a nav dropdown is open AND header is still transparent */}
      <div
        className={`hidden lg:block fixed inset-0 z-40 bg-black pointer-events-none transition-opacity duration-300 ${
          isAnyDesktopDropdownOpen && !isScrolled ? 'opacity-10' : 'opacity-0'
        }`}
        aria-hidden="true"
        data-testid="nav-hero-scrim"
      />
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/80 backdrop-blur-xl shadow-lg' : 'bg-transparent'
        }`}
        data-scrolled={isScrolled ? 'true' : 'false'}
      >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div
            className="flex-shrink-0 cursor-pointer"
            onClick={() => {
              if (location.pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                navigate('/');
              }
              setIsMobileMenuOpen(false);
            }}
            data-testid="header-logo"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_dc63fc88-a4c1-4ec0-a429-62db7388c03c/artifacts/qlcn8omg_ECOMM%20LOGO.PNG"
              alt="eCOMM Merchant Solutions"
              className={`h-8 min-[380px]:h-10 sm:h-12 w-auto transition-all duration-300 ${
                isScrolled ? '' : 'brightness-0 invert'
              }`}
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <div
              className="relative"
              onMouseEnter={handleProductsEnter}
              onMouseLeave={handleProductsLeave}
            >
              <button
                className={`flex items-center gap-1 transition-colors duration-200 font-medium ${
                  isScrolled ? 'text-gray-700 hover:text-[#1F4E79]' : 'text-white hover:text-gray-200'
                }`}
                data-testid="products-nav-button"
              >
                Products
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isDesktopProductsOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isDesktopProductsOpen && (
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-52 py-2 z-50 rounded-lg overflow-hidden border transition-colors duration-200 ${
                    isScrolled
                      ? 'bg-white shadow-xl border-gray-100'
                      : 'bg-white/10 backdrop-blur-md border-white/15 shadow-2xl shadow-black/20'
                  }`}
                  data-testid="products-dropdown"
                >
                  {productsItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        item.action();
                        setIsDesktopProductsOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 ${
                        isScrolled
                          ? 'text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                          : 'text-white hover:bg-white/15 hover:text-white'
                      }`}
                      data-testid={`products-dropdown-item-${index}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div
              className="relative"
              onMouseEnter={handleApiDropdownEnter}
              onMouseLeave={handleApiDropdownLeave}
            >
              <button
                className={`flex items-center gap-1 transition-colors duration-200 font-medium ${
                  isScrolled ? 'text-gray-700 hover:text-[#1F4E79]' : 'text-white hover:text-gray-200'
                }`}
                data-testid="api-docs-nav-button"
              >
                API Documentation
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isDesktopApiDocsOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isDesktopApiDocsOpen && (
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 py-2 z-50 rounded-lg overflow-hidden border transition-colors duration-200 ${
                    isScrolled
                      ? 'bg-white shadow-xl border-gray-100'
                      : 'bg-white/10 backdrop-blur-md border-white/15 shadow-2xl shadow-black/20'
                  }`}
                  data-testid="api-docs-dropdown"
                >
                  {apiDocsItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        item.action();
                        setIsDesktopApiDocsOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 ${
                        isScrolled
                          ? 'text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                          : 'text-white hover:bg-white/15 hover:text-white'
                      }`}
                      data-testid={`api-docs-dropdown-item-${index}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div
              className="relative"
              onMouseEnter={handleDropdownEnter}
              onMouseLeave={handleDropdownLeave}
            >
              <button
                className={`flex items-center gap-1 transition-colors duration-200 font-medium ${
                  isScrolled ? 'text-gray-700 hover:text-[#1F4E79]' : 'text-white hover:text-gray-200'
                }`}
                data-testid="resources-nav-button"
              >
                Resources
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isDesktopResourcesOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isDesktopResourcesOpen && (
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-56 py-2 z-50 rounded-lg overflow-hidden border transition-colors duration-200 ${
                    isScrolled
                      ? 'bg-white shadow-xl border-gray-100'
                      : 'bg-white/10 backdrop-blur-md border-white/15 shadow-2xl shadow-black/20'
                  }`}
                  data-testid="resources-dropdown"
                >
                  {resourcesItems.map((item, index) => (
                    item.action ? (
                      <button
                        key={index}
                        onClick={() => {
                          item.action();
                          setIsDesktopResourcesOpen(false);
                        }}
                        className={`block w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 ${
                          isScrolled
                            ? 'text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                            : 'text-white hover:bg-white/15 hover:text-white'
                        }`}
                        data-testid={`resources-dropdown-item-${index}`}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <a
                        key={index}
                        href={item.href}
                        className={`block px-4 py-2.5 text-sm transition-colors duration-150 ${
                          isScrolled
                            ? 'text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                            : 'text-white hover:bg-white/15 hover:text-white'
                        }`}
                        data-testid={`resources-dropdown-item-${index}`}
                      >
                        {item.label}
                      </a>
                    )
                  ))}
                </div>
              )}
            </div>
            <div
              className="relative"
              onMouseEnter={handleAboutEnter}
              onMouseLeave={handleAboutLeave}
            >
              <button
                className={`flex items-center gap-1 transition-colors duration-200 font-medium ${
                  isScrolled ? 'text-gray-700 hover:text-[#1F4E79]' : 'text-white hover:text-gray-200'
                }`}
                data-testid="about-nav-button"
              >
                About
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isDesktopAboutOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isDesktopAboutOpen && (
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-48 py-2 z-50 rounded-lg overflow-hidden border transition-colors duration-200 ${
                    isScrolled
                      ? 'bg-white shadow-xl border-gray-100'
                      : 'bg-white/10 backdrop-blur-md border-white/15 shadow-2xl shadow-black/20'
                  }`}
                  data-testid="about-dropdown"
                >
                  {aboutItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        item.action();
                        setIsDesktopAboutOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 ${
                        isScrolled
                          ? 'text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                          : 'text-white hover:bg-white/15 hover:text-white'
                      }`}
                      data-testid={`about-dropdown-item-${index}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div
              className="relative"
              onMouseEnter={handleLoginEnter}
              onMouseLeave={handleLoginLeave}
            >
              <button
                className={`flex items-center gap-1 transition-colors duration-200 font-medium ${
                  isScrolled ? 'text-gray-700 hover:text-[#1F4E79]' : 'text-white hover:text-gray-200'
                }`}
                data-testid="merchant-login-nav-button"
              >
                Merchant Login
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isDesktopLoginOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isDesktopLoginOpen && (
                <div
                  className={`absolute top-full right-0 mt-3 w-40 py-2 z-50 rounded-lg overflow-hidden border transition-colors duration-200 ${
                    isScrolled
                      ? 'bg-white shadow-xl border-gray-100'
                      : 'bg-white/10 backdrop-blur-md border-white/15 shadow-2xl shadow-black/20'
                  }`}
                  data-testid="merchant-login-dropdown"
                >
                  {loginItems.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={index}
                        onClick={() => {
                          item.action();
                          setIsDesktopLoginOpen(false);
                        }}
                        className={`flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm transition-colors duration-150 ${
                          isScrolled
                            ? 'text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
                            : 'text-white hover:bg-white/15 hover:text-white'
                        }`}
                        data-testid={`merchant-login-dropdown-item-${index}`}
                      >
                        <Icon size={16} className="flex-shrink-0" />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-3">
            {isDocsRoute && <DocsSearchTrigger />}
            <Button
              onClick={() => navigate('/contact-sales')}
              className="bg-[#1F4E79] hover:bg-[#163a5f] text-white transition-colors duration-200"
              data-testid="header-contact-sales-btn"
            >
              Contact Sales
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors duration-200 ${
              isScrolled ? 'hover:bg-gray-100' : 'hover:bg-white/20'
            }`}
            data-testid="mobile-menu-toggle"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? (
              <X size={24} className={isScrolled ? 'text-gray-900' : 'text-white'} />
            ) : (
              <Menu size={24} className={isScrolled ? 'text-gray-900' : 'text-white'} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu — slide-in side drawer with dim scrim */}
      {isMobileMenuOpen && (
        <>
          {/* Scrim */}
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setIsMobileMenuOpen(false)}
            className="lg:hidden fixed inset-0 top-20 z-40 bg-black/50 backdrop-blur-[2px] animate-[fadeIn_200ms_ease-out]"
            data-testid="mobile-menu-scrim"
          />
          {/* Drawer — sizes to content (no forced bottom) */}
          <div
            className="lg:hidden fixed top-20 right-0 z-50 w-[78%] max-w-[300px] max-h-[calc(100vh-5rem)] overflow-y-auto bg-white/95 backdrop-blur-xl border-l border-b border-white/40 shadow-2xl rounded-bl-2xl animate-[slideInRight_280ms_ease-out]"
            data-testid="mobile-menu-drawer"
          >
            <div className="px-4 py-4 space-y-3">
            <div>
              <button
                onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                className="flex items-center justify-between w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                data-testid="mobile-products-toggle"
              >
                Products
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isMobileProductsOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isMobileProductsOpen && (
                <div className="pl-4">
                  {productsItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        item.action();
                        setIsMobileMenuOpen(false);
                        setIsMobileProductsOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79] rounded-lg transition-colors duration-150"
                      data-testid={`mobile-products-item-${index}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <button
                onClick={() => setIsMobileApiDocsOpen(!isMobileApiDocsOpen)}
                className="flex items-center justify-between w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                data-testid="mobile-api-docs-toggle"
              >
                API Documentation
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isMobileApiDocsOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isMobileApiDocsOpen && (
                <div className="pl-4">
                  {apiDocsItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        item.action();
                        setIsMobileMenuOpen(false);
                        setIsMobileApiDocsOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79] rounded-lg transition-colors duration-150"
                      data-testid={`mobile-api-docs-item-${index}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <button
                onClick={() => setIsMobileResourcesOpen(!isMobileResourcesOpen)}
                className="flex items-center justify-between w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                data-testid="mobile-resources-toggle"
              >
                Resources
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isMobileResourcesOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isMobileResourcesOpen && (
                <div className="pl-4">
                  {resourcesItems.map((item, index) => (
                    item.action ? (
                      <button
                        key={index}
                        onClick={() => {
                          item.action();
                          setIsMobileMenuOpen(false);
                          setIsMobileResourcesOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79] rounded-lg transition-colors duration-150"
                        data-testid={`mobile-resources-item-${index}`}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <a
                        key={index}
                        href={item.href}
                        className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79] rounded-lg transition-colors duration-150"
                        data-testid={`mobile-resources-item-${index}`}
                      >
                        {item.label}
                      </a>
                    )
                  ))}
                </div>
              )}
            </div>
            <div>
              <button
                onClick={() => setIsMobileAboutOpen(!isMobileAboutOpen)}
                className="flex items-center justify-between w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                data-testid="mobile-about-toggle"
              >
                About
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isMobileAboutOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isMobileAboutOpen && (
                <div className="pl-4">
                  {aboutItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        item.action();
                        setIsMobileMenuOpen(false);
                        setIsMobileAboutOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79] rounded-lg transition-colors duration-150"
                      data-testid={`mobile-about-item-${index}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <button
                onClick={() => setIsMobileLoginOpen(!isMobileLoginOpen)}
                className="flex items-center justify-between w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                data-testid="mobile-merchant-login-toggle"
              >
                Merchant Login
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${isMobileLoginOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isMobileLoginOpen && (
                <div className="pl-4">
                  {loginItems.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={index}
                        onClick={() => {
                          item.action();
                          setIsMobileMenuOpen(false);
                          setIsMobileLoginOpen(false);
                        }}
                        className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79] rounded-lg transition-colors duration-150"
                        data-testid={`mobile-merchant-login-item-${index}`}
                      >
                        <Icon size={14} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <Button
              onClick={() => { setIsMobileMenuOpen(false); navigate('/contact-sales'); }}
              className="w-full bg-[#1F4E79] hover:bg-[#163a5f] text-white"
              data-testid="mobile-contact-sales-btn"
            >
              Contact Sales
            </Button>
          </div>
          </div>
        </>
      )}
    </header>
    </>
  );
};

export default Header;